import { TranslateService } from '@ngx-translate/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Component, Inject, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { getPhoneMask } from 'src/app/shared/utils/phone-mask.util';
import { User, ContextualMenuType } from 'src/app/settings/constants';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CompaniesService } from 'src/app/settings/services/companies.service';
import { CompanyUserService } from 'src/app/settings/services/company-user.service';
import { ContextualMenuService } from 'src/app/settings/services/contextual-menu.service';
import { updateValue, validateEmail, validatorUserName } from 'src/app/settings/validators';

@Component({
  selector: 'app-create-user',
  templateUrl: './create-user.component.html',
  styleUrls: ['./create-user.component.scss'],
})
export class CreateUserComponent implements OnInit {
  editUserForm: FormGroup;
  submited: boolean;

  contextualMenuType = ContextualMenuType;
  currentType: number;

  newUser: User;
  @Input() set editUser(value: User) {
    this.newUser = value;
    this.editUserForm.patchValue(this.newUser);
  }
  mask: string = '(+00) 0000000000000';
  isEdit: boolean = false;
  users: User[];
  isLoading: boolean = false;

  constructor(
    private readonly formBuilder: FormBuilder,
    public companiesService: CompaniesService,
    public dialogRef: MatDialogRef<CreateUserComponent>,
    public contextualMenuService: ContextualMenuService,
    private readonly translateService: TranslateService,
    private readonly snackBar: MatSnackBar,
    public companyUserService: CompanyUserService,
    @Inject(MAT_DIALOG_DATA) public data: { editUser: User; users: User[]; index: number }
  ) {
    this.editUserForm = this.formBuilder.group({
      name: ['', [Validators.required, (event) => validatorUserName('name', event, this.editUserForm, this.newUser)]],
      lastName: ['', [(event) => validatorUserName('lastName', event, this.editUserForm, this.newUser)]],
      email: ['', [Validators.required, (event) => validateEmail('email', event, this.editUserForm, this.newUser)]],
      phone: ['', [(event) => this.applyMask('phone', event)]],
    });
    this.users = this.data?.users || [];
  }

  ngOnInit(): void {
    if (this.data.editUser) {
      this.isEdit = true;
      this.newUser = this.data.editUser;
      this.editUserForm.patchValue(this.newUser);
      if (this.newUser.phone) this.mask = getPhoneMask(this.newUser.phone);
      if (!this.newUser.language) this.newUser.language = 'es';
    } else {
      this.newUser = new User(this.companiesService.currentCompany?.language || 'es');
      this.newUser.companyID = this.companiesService.currentCompany._id;
      this.newUser.companyName = this.companiesService.currentCompany.name;
      this.newUser.companyColor = this.companiesService.currentCompany.colorPalette?.primary;
      if (this.newUser.phone) this.mask = getPhoneMask(this.newUser.phone);
    }
  }

  applyMask(key, event) {
    if (!event.value || this.newUser[key] === event.value) return;
    updateValue(key, event.value, this.editUserForm, this.newUser);

    this.mask = getPhoneMask(event.value);
    return null;
  }

  closeModal(action) {
    this.newUser.expand = false;
    this.dialogRef.close({ status: action, newUser: this.newUser });
  }

  onContextalMenu(event, value, type, id) {
    this.currentType = type;
    const target = document.getElementById(id);
    this.contextualMenuService.processContextualMenuPosition(event, target, undefined, type);
    this.newUser.expand = value;
  }

  onCloseContextMenu() {
    this.newUser.expand = false;
  }

  setValue(value) {
    switch (this.currentType) {
      case ContextualMenuType.Language:
        this.newUser.language = value;
        break;
      case ContextualMenuType.Permission:
      default:
        this.newUser.role = value;
        break;
    }
    this.newUser.expand = false;
  }

  getError(event) {
    const field = this.editUserForm.get(event);
    return (this.submited || field?.touched) && (field?.errors?.required || field?.errors?.invalidParam);
  }

  getErrorType(event, type) {
    return this.editUserForm.get(event)?.errors?.[type];
  }

  getWhatsappError() {
    return this.editUserForm.controls.phone.status === 'INVALID';
  }

  testEmailAlreadyBeingUsedLocallyAndByOthers() {
    if (this.isEdit && this.data.editUser.email === this.newUser.email) {
      return false;
    }

    if (this.isEdit) {
      return this.users.some((user) => user.email === this.newUser.email && this.data.index !== user.index);
    }

    return this.users.some((user) => user.email === this.newUser.email);
  }

  doesThisEmailAlreadyExists() {
    return new Promise((resolve, reject) => {
      this.companyUserService
        .validateEmail(this.newUser.email, this.data.editUser?._id)
        .then((observable) => {
          observable.subscribe({
            next: (res: any) => {
              const checkEmailDb = res.code === 'emailExists' ? 'emailExists' : 'emailNotExists';

              const checkEmailLocally = this.testEmailAlreadyBeingUsedLocallyAndByOthers()
                ? 'emailExists'
                : 'emailNotExists';

              resolve(checkEmailDb === 'emailExists' || checkEmailLocally === 'emailExists');
            },
            error: (error) => {
              resolve(false);
            },
          });
        })
        .catch((error) => {
          resolve(false);
        });
    });
  }

  async onSubmit() {
    if (await this.doesThisEmailAlreadyExists()) {
      this.snackBar.open(
        `${this.translateService.instant('Shared.Error')}: ${this.translateService.instant(
          'Settings.Errors.EmailAlreadyExists'
        )}`,
        this.translateService.instant('Shared.Close'),
        { duration: 5000 }
      );
      return;
    }

    this.submited = true;

    if (this.editUserForm.valid && this.newUser.role) {
      this.newUser.expand = false;
      if (this.isEdit) {
        this.dialogRef.close({
          status: 'confirm',
          isEdit: this.isEdit,
          editUser: { _id: this.data.editUser._id, ...this.newUser, index: this.data.index },
        });
      } else {
        this.dialogRef.close({
          status: 'confirm',
          newUser: this.newUser,
        });
      }
    }
  }
}
